import React, { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { Textarea } from '../components/ui/Textarea';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { Plus, Filter, Download, Eye, Edit, Search, HardHat, FileText, GraduationCap, Stethoscope, Trash2, Calendar, AlertTriangle, Upload, CheckCircle2, X, ExternalLink, Save } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { PermissionGate } from '../components/PermissionGate';
import { MultiSelect } from '../components/ui/MultiSelect';
import { mockData } from '../services/mockData';
import { motion, AnimatePresence } from 'framer-motion';
const initialWorkersData = mockData.getWorkers();
// "Lavoratore" column is not disableable
const columnsConfig = [{
  key: 'name',
  label: 'Lavoratore',
  disableable: false
}, {
  key: 'company',
  label: 'Azienda',
  disableable: true
}, {
  key: 'role',
  label: 'Mansione',
  disableable: true
}, {
  key: 'trainingStatus',
  label: 'Formazione',
  disableable: true
}, {
  key: 'healthStatus',
  label: 'Sorv. sanitaria',
  disableable: true
}, {
  key: 'nextExpiry',
  label: 'Prox. scadenza',
  disableable: true
}, {
  key: 'actions',
  label: 'Azioni',
  disableable: true
}];
export function WorkersPage() {
  const {
    canCreate,
    canEdit,
    canDelete
  } = useRBAC();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [activeTab, setActiveTab] = useState<'info' | 'training' | 'health'>('info');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [importData, setImportData] = useState<any[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Form state for new worker
  const [formData, setFormData] = useState<any>({});
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialWorkersData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'company', 'fiscalCode']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  // Ensure "Lavoratore" column is always visible
  const handleColumnChange = (newColumns: string[]) => {
    if (!newColumns.includes('name')) {
      newColumns = ['name', ...newColumns];
    }
    setVisibleColumns(newColumns);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      code: '',
      nextVisitDate: '',
      notes: ''
    });
    setIsModalOpen(true);
  };
  const handleViewDetail = (item: any) => {
    setSelectedItem(item);
    setActiveTab('info');
    setIsDetailOpen(true);
  };
  const handleSave = () => {
    console.log('Saving worker:', formData);
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Simulate CSV parsing
      const mockParsedData = [{
        name: 'Mario Rossi',
        company: 'Azienda 1 SRL',
        role: 'Operaio',
        fiscalCode: 'RSSMRA80A01A123B'
      }, {
        name: 'Laura Bianchi',
        company: 'Azienda 1 SRL',
        role: 'Impiegata',
        fiscalCode: 'BNCLRA85B02B456C'
      }, {
        name: 'Giuseppe Verdi',
        company: 'Azienda 2 SRL',
        role: 'Tecnico',
        fiscalCode: 'VRDGPP78C03C789D'
      }];
      setImportData(mockParsedData);
      setImportStep('preview');
    }
  };
  const handleConfirmImport = () => {
    console.log('Importing workers:', importData);
    setImportStep('success');
    setTimeout(() => {
      setShowImportModal(false);
      setImportStep('upload');
      setImportData([]);
    }, 2000);
  };
  // Mock courses for detail view
  const mockCourses = [{
    id: 1,
    name: 'Sicurezza generale',
    date: '12/10/2020',
    expiry: '12/10/2025',
    status: 'valid',
    certificateUrl: '#'
  }, {
    id: 2,
    name: 'Primo soccorso',
    date: '01/01/2021',
    expiry: '01/01/2024',
    status: 'expired',
    certificateUrl: '#'
  }, {
    id: 3,
    name: 'Antincendio rischio medio',
    date: '15/03/2023',
    expiry: '15/03/2026',
    status: 'valid',
    certificateUrl: '#'
  }];
  return <Layout data-id="element-3008">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-3009">
        <div data-id="element-3010">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-3011">Lavoratori</h1>
          <p className="text-slate-500 mt-1" data-id="element-3012">
            Gestione dipendenti, formazione e sorveglianza sanitaria.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-3013">
          <Button variant="flat" color="secondary" startContent={<Upload className="h-4 w-4" data-id="element-3015" />} onClick={() => setShowImportModal(true)} data-id="element-3014">
            Import massivo
          </Button>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-3017" />} data-id="element-3016">
            Esporta
          </Button>
          <PermissionGate module="workers" action="create" data-id="element-3018">
            <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-3020" />} data-id="element-3019">
              Nuovo lavoratore
            </Button>
          </PermissionGate>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-3021">
        <div className="p-4 flex flex-col gap-4" data-id="element-3022">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-3023">
            <div className="flex-1" data-id="element-3024">
              <Input placeholder="Cerca lavoratore, azienda, CF..." startContent={<Search className="h-4 w-4" data-id="element-3026" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-3025" />
            </div>
            <div className="flex gap-2" data-id="element-3027">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-3029" />} data-id="element-3028">
                Filtri{' '}
                {Object.keys(filters).length > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-3030">
                    {Object.keys(filters).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig.filter(c => c.disableable !== false)} visibleColumns={visibleColumns.filter(v => v !== 'name')} onColumnChange={cols => handleColumnChange(['name', ...cols])} pageKey="workers" data-id="element-3031" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-3032">
              <Select label="Stato formazione" value={filters.trainingStatus || ''} onChange={e => setFilter('trainingStatus', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'valid',
            label: 'In regola'
          }, {
            value: 'expiring',
            label: 'In scadenza'
          }, {
            value: 'expired',
            label: 'Scaduto'
          }]} data-id="element-3033" />
              <Select label="Sorveglianza" value={filters.healthStatus || ''} onChange={e => setFilter('healthStatus', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'fit',
            label: 'Idoneo'
          }, {
            value: 'unfit',
            label: 'Non idoneo'
          }]} data-id="element-3034" />
              <div className="col-span-full flex justify-end" data-id="element-3035">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-3037" />} data-id="element-3036">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-3038">
        <div className="overflow-x-auto" data-id="element-3039">
          <table className="w-full text-left border-collapse table-fixed" data-id="element-3040">
            <thead data-id="element-3041">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3042">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-right w-28' : ''}`} data-id="element-3043">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-3044">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-3045">
                  {visibleColumns.includes('name') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-3046">
                      {item.name}
                    </td>}
                  {visibleColumns.includes('company') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-3047">
                      {item.company}
                    </td>}
                  {visibleColumns.includes('role') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-3048">
                      {item.role}
                    </td>}
                  {visibleColumns.includes('trainingStatus') && <td className="px-6 py-4" data-id="element-3049">
                      <Badge variant={item.trainingStatus === 'valid' ? 'success' : item.trainingStatus === 'expiring' ? 'warning' : 'danger'} data-id="element-3050">
                        {item.trainingStatus === 'valid' ? 'In regola' : item.trainingStatus === 'expiring' ? 'In scadenza' : 'Scaduto'}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('healthStatus') && <td className="px-6 py-4" data-id="element-3051">
                      <Badge variant={item.healthStatus === 'fit' ? 'success' : 'danger'} data-id="element-3052">
                        {item.healthStatus === 'fit' ? 'Idoneo' : 'Non idoneo'}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('nextExpiry') && <td className="px-6 py-4 text-sm font-mono text-slate-600" data-id="element-3053">
                      {item.nextExpiry}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-6 py-4 text-right" data-id="element-3054">
                      <div className="flex items-center justify-end gap-2" data-id="element-3055">
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleViewDetail(item)} data-id="element-3056">
                          <Eye className="h-4 w-4 text-slate-500" data-id="element-3057" />
                        </Button>
                        <PermissionGate module="workers" action="edit" data-id="element-3058">
                          <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-3059">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-3060" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-3061">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3062" />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica lavoratore' : 'Nuovo lavoratore'} size="2xl" data-id="element-3063">
        <AnimatePresence mode="wait" data-id="element-3064">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-3065">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3066">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-3067" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3068">
                Lavoratore salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3069">
              <div className="space-y-8" data-id="element-3070">
                <section data-id="element-3071">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3072">
                    <HardHat className="h-4 w-4" data-id="element-3073" /> Dati personali
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3074">
                    <Input label="Nome e cognome" placeholder="Mario Rossi" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required data-id="element-3075" />
                    <ProgressiveNumberInput type="worker" value={formData.code} onChange={val => setFormData({
                  ...formData,
                  code: val
                })} disabled={false} data-id="element-3076" />
                    <Input label="Codice fiscale" placeholder="RSSMRA..." value={formData.fiscalCode || ''} onChange={e => setFormData({
                  ...formData,
                  fiscalCode: e.target.value
                })} required data-id="element-3077" />
                    <Input label="Data di nascita" type="date" value={formData.birthDate || ''} onChange={e => setFormData({
                  ...formData,
                  birthDate: e.target.value
                })} data-id="element-3078" />
                    <Input label="Luogo di nascita" placeholder="Città (PV)" value={formData.birthPlace || ''} onChange={e => setFormData({
                  ...formData,
                  birthPlace: e.target.value
                })} data-id="element-3079" />
                    <Input label="Email personale" type="email" placeholder="mario.rossi@email.it" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} required description="Fondamentale per invio attestati" data-id="element-3080" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-3081"></div>

                <section data-id="element-3082">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3083">
                    <FileText className="h-4 w-4" data-id="element-3084" /> Rapporto di lavoro
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3085">
                    <Select label="Azienda" value={formData.company || ''} onChange={e => setFormData({
                  ...formData,
                  company: e.target.value
                })} options={[{
                  value: 'Azienda 1 SRL',
                  label: 'Azienda 1 SRL'
                }, {
                  value: 'Azienda 2 SRL',
                  label: 'Azienda 2 SRL'
                }]} required data-id="element-3086" />
                    <Input label="Mansione" value={formData.role || ''} onChange={e => setFormData({
                  ...formData,
                  role: e.target.value
                })} data-id="element-3087" />
                    <MultiSelect label="Ruoli sicurezza" options={[{
                  value: 'rls',
                  label: 'RLS'
                }, {
                  value: 'preposto',
                  label: 'Preposto'
                }, {
                  value: 'antincendio',
                  label: 'Addetto antincendio'
                }, {
                  value: 'primosoccorso',
                  label: 'Addetto primo soccorso'
                }]} value={formData.safetyRoles || []} onChange={val => setFormData({
                  ...formData,
                  safetyRoles: val
                })} data-id="element-3088" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-3089"></div>

                <section data-id="element-3090">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3091">
                    <Stethoscope className="h-4 w-4" data-id="element-3092" /> Sorveglianza sanitaria
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3093">
                    <Input label="Data prossima visita" type="date" value={formData.nextVisitDate || ''} onChange={e => setFormData({
                  ...formData,
                  nextVisitDate: e.target.value
                })} data-id="element-3094" />
                    <div className="col-span-full" data-id="element-3095">
                      <Textarea label="Note" placeholder="Eventuali note sulla sorveglianza sanitaria..." value={formData.notes || ''} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} data-id="element-3096" />
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3097">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-3098">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3100" />} data-id="element-3099">
                    Salva lavoratore
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} title="Scheda lavoratore" size="2xl" data-id="element-3101">
        {selectedItem && <div className="flex flex-col h-[70vh]" data-id="element-3102">
            {/* Header */}
            <div className="flex items-start gap-4 mb-6" data-id="element-3103">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-2xl font-bold text-slate-400" data-id="element-3104">
                {selectedItem.name.substring(0, 2).toUpperCase()}
              </div>
              <div data-id="element-3105">
                <h2 className="text-2xl font-bold text-seform-navy" data-id="element-3106">
                  {selectedItem.name}
                </h2>
                <p className="text-slate-500" data-id="element-3107">
                  {selectedItem.role} presso{' '}
                  <span className="font-semibold text-seform-orange" data-id="element-3108">
                    {selectedItem.company}
                  </span>
                </p>
                <div className="flex gap-2 mt-2" data-id="element-3109">
                  <Badge variant={selectedItem.trainingStatus === 'valid' ? 'success' : 'warning'} data-id="element-3110">
                    Formazione:{' '}
                    {selectedItem.trainingStatus === 'valid' ? 'OK' : 'Scaduta'}
                  </Badge>
                  <Badge variant={selectedItem.healthStatus === 'fit' ? 'success' : 'danger'} data-id="element-3111">
                    Visita:{' '}
                    {selectedItem.healthStatus === 'fit' ? 'Idoneo' : 'Non idoneo'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-200 mb-6" data-id="element-3112">
              <button className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'info' ? 'border-seform-orange text-seform-orange' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('info')} data-id="element-3113">
                Anagrafica
              </button>
              <button className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'training' ? 'border-seform-orange text-seform-orange' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('training')} data-id="element-3114">
                Formazione
              </button>
              <button className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'health' ? 'border-seform-orange text-seform-orange' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('health')} data-id="element-3115">
                Sorveglianza sanitaria
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar" data-id="element-3116">
              {activeTab === 'info' && <div className="grid grid-cols-2 gap-6" data-id="element-3117">
                  <div className="space-y-1" data-id="element-3118">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3119">
                      Codice fiscale
                    </span>
                    <p className="font-medium" data-id="element-3120">RSSMRA80A01A123B</p>
                  </div>
                  <div className="space-y-1" data-id="element-3121">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3122">
                      Email
                    </span>
                    <p className="font-medium" data-id="element-3123">mario.rossi@email.it</p>
                  </div>
                  <div className="space-y-1" data-id="element-3124">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3125">
                      Tipo contratto
                    </span>
                    <p className="font-medium" data-id="element-3126">Indeterminato</p>
                  </div>
                  <div className="space-y-1" data-id="element-3127">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3128">
                      Ruoli
                    </span>
                    <div className="flex gap-1 flex-wrap" data-id="element-3129">
                      <Badge size="sm" data-id="element-3130">RLS</Badge>
                      <Badge size="sm" data-id="element-3131">Preposto</Badge>
                    </div>
                  </div>
                </div>}

              {activeTab === 'training' && <div className="space-y-4" data-id="element-3132">
                  <div className="flex justify-between items-center" data-id="element-3133">
                    <h4 className="font-bold text-seform-navy" data-id="element-3134">
                      Corsi effettuati
                    </h4>
                    <Button size="sm" startContent={<Plus className="h-4 w-4" data-id="element-3136" />} data-id="element-3135">
                      Aggiungi corso
                    </Button>
                  </div>
                  <div className="border border-slate-100 rounded-xl overflow-hidden" data-id="element-3137">
                    <table className="w-full text-sm text-left" data-id="element-3138">
                      <thead className="bg-slate-50 text-slate-500 font-semibold" data-id="element-3139">
                        <tr data-id="element-3140">
                          <th className="p-3" data-id="element-3141">Corso</th>
                          <th className="p-3" data-id="element-3142">Data</th>
                          <th className="p-3" data-id="element-3143">Scadenza</th>
                          <th className="p-3" data-id="element-3144">Stato</th>
                          <th className="p-3" data-id="element-3145">Attestato</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-3146">
                        {mockCourses.map(course => <tr key={course.id} data-id="element-3147">
                            <td className="p-3 font-medium" data-id="element-3148">{course.name}</td>
                            <td className="p-3" data-id="element-3149">{course.date}</td>
                            <td className="p-3" data-id="element-3150">{course.expiry}</td>
                            <td className="p-3" data-id="element-3151">
                              <Badge variant={course.status === 'valid' ? 'success' : 'danger'} size="sm" data-id="element-3152">
                                {course.status === 'valid' ? 'Valido' : 'Scaduto'}
                              </Badge>
                            </td>
                            <td className="p-3" data-id="element-3153">
                              <Button variant="ghost" size="sm" className="!px-2 text-seform-orange" onClick={() => window.open(course.certificateUrl, '_blank')} data-id="element-3154">
                                <ExternalLink className="h-4 w-4" data-id="element-3155" />
                                <span className="ml-1" data-id="element-3156">Vedi</span>
                              </Button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {activeTab === 'health' && <div className="space-y-4" data-id="element-3157">
                  <div className="flex justify-between items-center" data-id="element-3158">
                    <h4 className="font-bold text-seform-navy" data-id="element-3159">
                      Visite mediche
                    </h4>
                    <Button size="sm" startContent={<Plus className="h-4 w-4" data-id="element-3161" />} data-id="element-3160">
                      Aggiungi visita
                    </Button>
                  </div>
                  <div className="border border-slate-100 rounded-xl overflow-hidden" data-id="element-3162">
                    <table className="w-full text-sm text-left" data-id="element-3163">
                      <thead className="bg-slate-50 text-slate-500 font-semibold" data-id="element-3164">
                        <tr data-id="element-3165">
                          <th className="p-3" data-id="element-3166">Tipo visita</th>
                          <th className="p-3" data-id="element-3167">Data</th>
                          <th className="p-3" data-id="element-3168">Esito</th>
                          <th className="p-3" data-id="element-3169">Scadenza</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-3170">
                        <tr data-id="element-3171">
                          <td className="p-3" data-id="element-3172">Periodica</td>
                          <td className="p-3" data-id="element-3173">10/01/2024</td>
                          <td className="p-3" data-id="element-3174">
                            <Badge variant="success" size="sm" data-id="element-3175">
                              Idoneo
                            </Badge>
                          </td>
                          <td className="p-3" data-id="element-3176">10/01/2025</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>}
            </div>
          </div>}
      </Modal>

      {/* Import Modal */}
      <Modal isOpen={showImportModal} onClose={() => {
      setShowImportModal(false);
      setImportStep('upload');
      setImportData([]);
    }} title="Import massivo lavoratori" size="lg" data-id="element-3177">
        <AnimatePresence mode="wait" data-id="element-3178">
          {importStep === 'upload' && <motion.div key="upload" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="space-y-6" data-id="element-3179">
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center" data-id="element-3180">
                <Upload className="h-12 w-12 text-slate-300 mx-auto mb-4" data-id="element-3181" />
                <p className="text-slate-600 mb-2" data-id="element-3182">
                  Trascina qui il file CSV o clicca per selezionarlo
                </p>
                <p className="text-sm text-slate-400 mb-4" data-id="element-3183">
                  Formato supportato: CSV con colonne Nome, Azienda, Mansione,
                  CF
                </p>
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileUpload} data-id="element-3184" />
                <Button onClick={() => fileInputRef.current?.click()} startContent={<Upload className="h-4 w-4" data-id="element-3186" />} data-id="element-3185">
                  Seleziona file
                </Button>
              </div>
            </motion.div>}

          {importStep === 'preview' && <motion.div key="preview" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="space-y-6" data-id="element-3187">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2" data-id="element-3188">
                <CheckCircle2 className="h-5 w-5 text-green-600" data-id="element-3189" />
                <span className="text-green-700 font-medium" data-id="element-3190">
                  {importData.length} lavoratori trovati nel file
                </span>
              </div>
              <div className="border border-slate-100 rounded-xl overflow-hidden max-h-64 overflow-y-auto" data-id="element-3191">
                <table className="w-full text-sm text-left" data-id="element-3192">
                  <thead className="bg-slate-50 text-slate-500 font-semibold sticky top-0" data-id="element-3193">
                    <tr data-id="element-3194">
                      <th className="p-3" data-id="element-3195">Nome</th>
                      <th className="p-3" data-id="element-3196">Azienda</th>
                      <th className="p-3" data-id="element-3197">Mansione</th>
                      <th className="p-3" data-id="element-3198">CF</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-3199">
                    {importData.map((row, i) => <tr key={i} data-id="element-3200">
                        <td className="p-3 font-medium" data-id="element-3201">{row.name}</td>
                        <td className="p-3" data-id="element-3202">{row.company}</td>
                        <td className="p-3" data-id="element-3203">{row.role}</td>
                        <td className="p-3 font-mono text-xs" data-id="element-3204">
                          {row.fiscalCode}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3" data-id="element-3205">
                <Button variant="flat" color="default" onClick={() => setImportStep('upload')} data-id="element-3206">
                  Indietro
                </Button>
                <Button onClick={handleConfirmImport} startContent={<CheckCircle2 className="h-4 w-4" data-id="element-3208" />} data-id="element-3207">
                  Conferma import
                </Button>
              </div>
            </motion.div>}

          {importStep === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-12" data-id="element-3209">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3210">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-3211" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3212">
                Import completato con successo
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-3213">
                {importData.length} lavoratori importati
              </p>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}